import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { sv, enUS } from "date-fns/locale";
import { useLanguage } from "@/context/LanguageContext";
import { commonDateLabels } from "@/i18n";
import { CalendarIcon, Clock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { toInputDate } from "@/utils/dateUtils";


interface DateTimePickerProps {
  value: string; // ISO string
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  showTime?: boolean; // New parameter to control time selection
  minDaysAhead?: number; // Minimum days ahead (e.g., 2 for buyer delivery)
  allowPastDates?: boolean; // When true, past dates can be selected (e.g. bug reports)
  renderTimeExtra?: React.ReactNode; // Extra content rendered inline with time selects
}

export const DateTimePicker = ({
  value,
  onChange,
  label,
  className,
  showTime = true,
  minDaysAhead = 0,
  allowPastDates = false,
  renderTimeExtra,
}: DateTimePickerProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { language } = useLanguage();
  const dateLabels = commonDateLabels(language);
  const dateLocale = language === "en" ? enUS : sv;
  const { isNative } = useNativePlatform();

  // Parse the ISO string to Date and time components
  const date = value ? new Date(value) : new Date();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  // Calculate minimum date if minDaysAhead is specified
  const getMinDate = () => {
    if (allowPastDates) return undefined;
    if (minDaysAhead > 0) {
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + minDaysAhead);
      minDate.setHours(0, 0, 0, 0);
      return minDate;
    }
    return new Date(); // Today
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // If showTime is false, set time to 00:00
      if (!showTime) {
        selectedDate.setHours(0, 0, 0, 0);
      } else {
        // Preserve current time
        selectedDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
      }
      onChange(selectedDate.toISOString());
    }
    setIsCalendarOpen(false);
  };

  const handleTimeChange = (type: "hour" | "minute", value: string) => {
    if (!showTime) return;

    const newDate = new Date(date);
    if (type === "hour") {
      newDate.setHours(parseInt(value), newDate.getMinutes(), 0, 0);
    } else {
      newDate.setHours(newDate.getHours(), parseInt(value), 0, 0);
    }
    onChange(newDate.toISOString());
  };

  const handleNativeDateChange = (dateStr: string) => {
    if (!dateStr) return;
    const [y, m, d] = dateStr.split("-").map(Number);
    const newDate = new Date(y, m - 1, d);
    if (!showTime) {
      newDate.setHours(0, 0, 0, 0);
    } else {
      newDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
    }
    onChange(newDate.toISOString());
  };

  const handleNativeTimeChange = (timeStr: string) => {
    if (!timeStr || !showTime) return;
    const [h, min] = timeStr.split(":").map(Number);
    const newDate = new Date(date);
    newDate.setHours(h, min, 0, 0);
    onChange(newDate.toISOString());
  };

  if (isNative) {
    const minDate = getMinDate();
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {label && <span className="text-sm font-medium">{label}</span>}
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Input
              type="date"
              value={value ? toInputDate(date) : ""}
              min={minDate ? toInputDate(minDate) : undefined}
              onChange={(e) => handleNativeDateChange(e.target.value)}
              className="pr-9 [&::-webkit-calendar-picker-indicator]:opacity-100"
            />
            <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>
          {showTime && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                type="time"
                value={`${hours}:${minutes}`}
                onChange={(e) => handleNativeTimeChange(e.target.value)}
                className="flex-1 [&::-webkit-calendar-picker-indicator]:opacity-100"
              />
              {renderTimeExtra}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && <span className="text-sm font-medium">{label}</span>}

      <div className="flex flex-col gap-2">
        {/* Date Picker */}
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !value && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? (
                format(date, "PPP", { locale: dateLocale })
              ) : (
                <span>{dateLabels.selectDate ?? "Select date"}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              initialFocus
              disabled={(date) => {
                const minDate = getMinDate();
                if (!minDate) return false;
                // Compare dates without time to allow selecting today even if minDaysAhead is 0
                const dateOnly = new Date(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate(),
                );
                const minDateOnly = new Date(
                  minDate.getFullYear(),
                  minDate.getMonth(),
                  minDate.getDate(),
                );
                return dateOnly < minDateOnly;
              }}
            />
          </PopoverContent>
        </Popover>

        {/* Time Picker - Only show if showTime is true */}
        {showTime && (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Select
              value={hours}
              onValueChange={(value) => handleTimeChange("hour", value)}
            >
              <SelectTrigger className="w-16">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={i.toString().padStart(2, "0")}>
                    {i.toString().padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-lg font-medium text-muted-foreground">:</span>
            <Select
              value={minutes}
              onValueChange={(value) => handleTimeChange("minute", value)}
            >
              <SelectTrigger className="w-16">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 60 }, (_, i) => (
                  <SelectItem key={i} value={i.toString().padStart(2, "0")}>
                    {i.toString().padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {renderTimeExtra}
          </div>
        )}
      </div>
    </div>
  );
};
